
import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Backdrop from "@mui/material/Backdrop";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import MDButton from "components/MDButton";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MDTypography from "components/MDTypography";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import CircularProgress from "@mui/material/CircularProgress";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import SocialStashModal from "layouts/social-stash/SocialStashModal";
import SocialStudioModal from "layouts/social-studio/SocialStudioModal";
import SocialReportingModal from "layouts/social-reporting/SocialReportingModal";
import Plan from "layouts/profile/components/Subscription/plan";

// Auth imports
import { AuthContext } from "context";
import AuthService from "services/auth-service";
import UserService from "services/user-service";
import { useNavigate } from "react-router-dom";

/**
 * Props now include:
 * - onUsageChanged  ⭐ required for refreshing usage from parent
 */
function DowngradeAlert({
  open,
  onClose,
  profilesUsed,
  profilesLimit,
  snapshotsUsed,
  snapshotsLimit,
  reportsUsed,   // [NEW]
  reportsLimit,  // [NEW]
  onUsageChanged,   // ⭐ IMPORTANT
}) {
  const [showCard, setShowCard] = useState(false);

  // "main" | "stash" | "studio" | "upgrade"
  const [activeView, setActiveView] = useState("main");

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setShowCard(true);
    } else {
      const t = setTimeout(() => setShowCard(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  // ⭐ The correct approach: let parent re-check usage
  const goBack = async () => {
    await onUsageChanged();      // Refresh usage in parent
    setActiveView("main");
  };

  const handleLogOut = async () => {
    await AuthService.logout();
    authContext.logout();
    navigate("/authentication/sign-in");
  };

  // Kebab menu state
  const [kebabAnchor, setKebabAnchor] = useState(null);
  const handleKebabOpen = (e) => setKebabAnchor(e.currentTarget);
  const handleKebabClose = () => setKebabAnchor(null);

  // Workspace switcher state
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

  const handleWorkspaceMenuOpen = async (e) => {
    setWorkspaceMenuAnchor(e.currentTarget);
    setLoadingWorkspaces(true);
    try {
      const response = await UserService.fetchUserWorkspaces();
      const list = Array.isArray(response) ? response : (response.data || []);
      setWorkspaces(list);
    } catch (err) {
      console.error("Failed to fetch workspaces", err);
    } finally {
      setLoadingWorkspaces(false);
    }
  };
  const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);
  const handleSwitchWorkspace = async (workspaceId) => {
    try {
      await UserService.switchActiveWorkspace(workspaceId);
      localStorage.setItem("activeWorkspaceId", workspaceId);
      window.dispatchEvent(new CustomEvent("profile-updated"));
      handleWorkspaceMenuClose();
      handleKebabClose();
    } catch (err) {
      console.error("Failed to switch workspace", err);
    }
  };

  const isProfilesOver = profilesUsed > profilesLimit;
  const isSnapshotsOver = snapshotsUsed > snapshotsLimit;
  const isReportsOver = reportsUsed > reportsLimit; // [NEW]

  return (
    <Backdrop
      open={open || showCard}
      sx={{
        zIndex: (t) => t.zIndex.modal + 2,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
      }}
    >
      <Slide direction="up" in={showCard} mountOnEnter unmountOnExit>
        <Card
          sx={{
            width: "95%",
            maxWidth: 950,
            maxHeight: "90vh",
            overflowY: "auto",
            p: 2,
            backgroundColor: "white",
            borderRadius: "16px",
            position: "relative",
            boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
          }}
        >

          {/* 3-dot kebab menu — top right, only on main view */}
          {activeView === "main" && (
            <>
              <IconButton
                size="small"
                onClick={handleKebabOpen}
                sx={{ position: "absolute", top: 8, right: 8, color: "text.secondary", zIndex: 11 }}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={kebabAnchor}
                open={Boolean(kebabAnchor)}
                onClose={handleKebabClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{ mt: 0.5, zIndex: 9999 }}
              >
                <MenuItem onClick={handleWorkspaceMenuOpen}>
                  <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                  Switch Workspace
                </MenuItem>
                <Menu
                  anchorEl={workspaceMenuAnchor}
                  open={Boolean(workspaceMenuAnchor)}
                  onClose={handleWorkspaceMenuClose}
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{ zIndex: 10000 }}
                >
                  {loadingWorkspaces ? (
                    <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading...</MenuItem>
                  ) : workspaces.length > 0 ? (
                    workspaces.map((ws) => (
                      <MenuItem
                        key={ws.workspace_id?._id || ws._id}
                        onClick={() => handleSwitchWorkspace(ws.workspace_id?._id || ws._id)}
                        selected={ws.is_active}
                      >
                        {ws.workspace_id?.name || "Unknown"} {ws.is_active && "(Active)"}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No other workspaces</MenuItem>
                  )}
                </Menu>
                <MenuItem onClick={() => { handleKebabClose(); handleLogOut(); }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Log Out
                </MenuItem>
              </Menu>
            </>
          )}

          {/* BACK BUTTON */}
          {activeView !== "main" && (
            <MDButton
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={goBack}
              sx={{
                position: "sticky",
                top: -25,
                left: 0,
                zIndex: 10,
                background: "white",
                color: "#333 !important",
                fontWeight: "bold",
                textTransform: "none",
                borderBottom: "1px solid #eee",
                mt: "-8px",
                mb: 2,
                "&:hover": {
                  backgroundColor: "white !important",
                  color: "#333 !important",
                },
              }}
            >
              Back
            </MDButton>
          )}

          {/* MAIN VIEW */}
          {activeView === "main" && (
            <>
              <Box display="flex" flexDirection="column" alignItems="center" mb={2} mt={1}>
                <WarningIcon sx={{ fontSize: 60, color: "#FFB800", mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  Subscription Downgrade Notice
                </Typography>
                <Typography variant="body2" color="textSecondary" mt={1} textAlign="center">
                  You have exceeded your downgraded plan limits.
                  You may delete excess items or upgrade your plan.
                </Typography>
              </Box>

              <MDBox
                sx={{
                  borderRadius: "12px",
                  backgroundColor: "#f0f2f5",
                  padding: "12px",
                }}
              >
                <Grid container spacing={2}>

                  {/* ==== PROFILES ==== */}
                  <Grid item xs={12} md={4}>
                    <Card>
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        p={2}
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                        }}
                      >
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                          Social Stash (Profiles)
                        </MDTypography>

                        <MDBox display="flex" flexDirection="column" alignItems="center" mb={1}>
                          <MDTypography variant="body2">
                            (<strong>{profilesUsed}</strong> consumed /
                            <strong>{profilesLimit}</strong> allowed)
                          </MDTypography>
                          {isProfilesOver ? (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <ErrorIcon sx={{ color: "#d9534f", mr: 0.5, fontSize: "1rem" }} />
                              <span style={{ color: "#d9534f", fontWeight: 600, fontSize: "0.875rem" }}>
                                Over by <strong>{profilesUsed - profilesLimit}</strong>
                              </span>
                            </Box>
                          ) : (
                            <CheckCircleIcon sx={{ color: "#28a745", mt: 0.5 }} />
                          )}
                        </MDBox>

                        <MDButton
                          variant="contained"
                          color="accent"
                          size="small"
                          // startIcon={<DeleteForeverIcon />}
                          onClick={() => setActiveView("stash")}
                          sx={{ mt: "auto" }}
                        >
                          Manage Profiles
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* ==== SNAPSHOTS ==== */}
                  <Grid item xs={12} md={4}>
                    <Card>
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        p={2}
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                        }}
                      >
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                          Social Studio (Snapshots)
                        </MDTypography>

                        <MDBox display="flex" flexDirection="column" alignItems="center" mb={1}>
                          <MDTypography variant="body2">
                            (<strong>{snapshotsUsed}</strong> consumed /
                            <strong>{snapshotsLimit}</strong> allowed)
                          </MDTypography>
                          {isSnapshotsOver ? (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <ErrorIcon sx={{ color: "#d9534f", mr: 0.5, fontSize: "1rem" }} />
                              <span style={{ color: "#d9534f", fontWeight: 600, fontSize: "0.875rem" }}>
                                Over by <strong>{snapshotsUsed - snapshotsLimit}</strong>
                              </span>
                            </Box>
                          ) : (
                            <CheckCircleIcon sx={{ color: "#28a745", mt: 0.5 }} />
                          )}
                        </MDBox>

                        <MDButton
                          variant="contained"
                          color="accent"
                          size="small"
                          // startIcon={<DeleteForeverIcon />}
                          onClick={() => setActiveView("studio")}
                          sx={{ mt: "auto" }}
                        >
                          Manage Snapshots
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* ==== REPORTS ==== */}
                  <Grid item xs={12} md={4}>
                    <Card>
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        p={2}
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                        }}
                      >
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                          Social Studio (Reports)
                        </MDTypography>

                        <MDBox display="flex" flexDirection="column" alignItems="center" mb={1}>
                          <MDTypography variant="body2">
                            (<strong>{reportsUsed || 0}</strong> consumed /
                            <strong>{reportsLimit || 0}</strong> allowed)
                          </MDTypography>
                          {isReportsOver ? (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <ErrorIcon sx={{ color: "#d9534f", mr: 0.5, fontSize: "1rem" }} />
                              <span style={{ color: "#d9534f", fontWeight: 600, fontSize: "0.875rem" }}>
                                Over by <strong>{(reportsUsed || 0) - (reportsLimit || 0)}</strong>
                              </span>
                            </Box>
                          ) : (
                            <CheckCircleIcon sx={{ color: "#28a745", mt: 0.5 }} />
                          )}
                        </MDBox>

                        <MDButton
                          variant="contained"
                          color="accent"
                          size="small"
                          onClick={() => setActiveView("reports")}
                          sx={{ mt: "auto" }}
                        >
                          Manage Reports
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>

                  {/* ==== UPGRADE PLAN ==== */}
                  <Grid item xs={12}>
                    <Card>
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        p={2}
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                        }}
                      >
                        <MDTypography variant="h6" fontWeight="bold" mb={1}>
                          Upgrade Your Plan
                        </MDTypography>

                        <Typography variant="body2" mb={2}>
                          Increase your profile and snapshot limits instantly.
                        </Typography>

                        <MDButton
                          variant="contained"
                          color="accent"
                          size="small"
                          onClick={() => setActiveView("upgrade")}
                        >
                          Upgrade Plan
                        </MDButton>
                      </MDBox>
                    </Card>
                  </Grid>

                </Grid>
              </MDBox>
            </>
          )}

          {/* PROFILE CLEANUP MODAL */}
          {activeView === "stash" && <SocialStashModal />}

          {/* SNAPSHOT CLEANUP MODAL */}
          {activeView === "studio" && <SocialStudioModal />}

          {/* REPORTS CLEANUP MODAL */}
          {activeView === "reports" && <SocialReportingModal />}

          {/* UPGRADE PLAN VIEW */}
          {activeView === "upgrade" && (
            <Box sx={{ mt: 2 }}>
              <Plan />
            </Box>
          )}

        </Card>
      </Slide>
    </Backdrop>
  );
}

export default DowngradeAlert;
